/**
 * Docs on all available custom events:
 * https://www.notion.so/softr/Custom-Codes-in-React-Blocks-b1f43c597a1045948274ebbf829bb24d
 */

import { type AnyElementAction } from "./types/actions";
import type { RecordItem } from "./types/record";
import { isParentSameOrigin } from "./window";
import { FormEnding } from "./types";

export const dispatchEvent = (event: CustomEvent) => {
    /**
     * In cases where the block is inside an iframe (e.g. modal), we want to dispatch the event to the parent window as well.
     */
    if (isParentSameOrigin() && window.parent !== window) {
        const newEvent = new CustomEvent(event.type, { detail: event.detail });
        Object.defineProperty(newEvent, "fromChildFrame", { value: true });
        window.parent.dispatchEvent(newEvent);
    }

    window.dispatchEvent(event);
};

export const blockLoadedEvent = (hrid: string) => {
    return new CustomEvent(`block-loaded-${hrid}`);
};

export const blockUnloadEvent = (hrid: string) => {
    return new CustomEvent(`block-unload-${hrid}`);
};

// When a given record is not found, this event must be called with an empty object `{}`
export const getRecordEvent = (hrid: string, record: RecordItem | Record<never, undefined>) => {
    return new CustomEvent(`get-record-${hrid}`, { detail: record });
};
export const itemDetailsHiddenEvent = (id: string) => {
    return new CustomEvent(`item-details-hidden-${id}`);
};
interface CustomEventsMap {
    [event: `get-record-${string}`]: CustomEvent<
        | RecordItem
        // When there is no record returned by the backend, the payload is an empty object instead of `RecordItem`
        | Record<never, undefined>
    >;
    [event: `item-details-hidden-${string}`]: CustomEvent;
    "modal-current-record": CustomEvent<ModalCurrentRecordData | null>;
}
export type CustomEventListener<TEvent extends keyof CustomEventsMap> = (event: CustomEventsMap[TEvent]) => void;
export function addCustomEventListener<TEvent extends keyof CustomEventsMap>(
    event: TEvent,
    handler: (event: CustomEventsMap[TEvent]) => void,
    options?: AddEventListenerOptions,
) {
    // @ts-expect-error: Suppressed automatically in TS 5.8 to 6 migration
    window.addEventListener(event, handler, options);
}
export function removeCustomEventListener<TEvent extends keyof CustomEventsMap>(
    event: TEvent,
    handler: (event: CustomEventsMap[TEvent]) => void,
) {
    // @ts-expect-error: Suppressed automatically in TS 5.8 to 6 migration
    window.removeEventListener(event, handler);
}

export const getRecordsBeforeEvent = (hrid: string, payload: unknown) => {
    return new CustomEvent(`get-records-${hrid}:before`, { detail: payload });
};

export const getRecordsEvent = (hrid: string, records: RecordItem[]) => {
    return new CustomEvent(`get-records-${hrid}`, { detail: records });
};

interface UpdateRecordSuccessEventPayload {
    /**
     * ID of the record that was updated.
     */
    id: string | number;
    /**
     * Form values that were submitted.
     */
    payload: Record<string, unknown>;
    /**
     * Updated record after submission.
     *
     * @TODO: the actual type here should be `UnknownRecord` but it's not part of SDK yet.
     */
    response: unknown;
    action?: AnyElementAction;
}

export const updateRecordSuccessEvent = (hrid: string, payload: UpdateRecordSuccessEventPayload) => {
    return new CustomEvent(`update-record-success-${hrid}`, { detail: payload });
};

export const updateRecordFailureEvent = (hrid: string, error: unknown) => {
    return new CustomEvent(`update-record-failure-${hrid}`, { detail: error });
};

export const upvoteRecordSuccessEvent = (hrid: string, payload?: Record<string, unknown>) => {
    return new CustomEvent(`upvote-record-success-${hrid}`, { detail: payload });
};

export const upvoteRecordFailureEvent = (hrid: string, error: unknown) => {
    return new CustomEvent(`upvote-record-failure-${hrid}`, { detail: error });
};

export const submitFormEvent = (hrid: string, data: Record<string, unknown>) => {
    return new CustomEvent(`submit-form-${hrid}`, { detail: data });
};

export const submitFormSuccessEvent = (
    hrid: string,
    payload: Record<string, unknown>,
    response: unknown,
    ending?: FormEnding,
) => {
    return new CustomEvent(`submit-form-success-${hrid}`, { detail: { payload, response, ending } });
};

export const submitFormFailureEvent = (hrid: string, error: unknown) => {
    return new CustomEvent(`submit-form-failure-${hrid}`, { detail: error });
};

export const addRecordEvent = (hrid: string, data: Record<string, unknown>) => {
    return new CustomEvent(`add-record-${hrid}`, { detail: data });
};

export const addRecordSuccessEvent = (hrid: string, payload: Record<string, unknown>, response: unknown) => {
    return new CustomEvent(`add-record-success-${hrid}`, { detail: { payload, response } });
};

export const addRecordFailureEvent = (hrid: string, error: unknown) => {
    return new CustomEvent(`add-record-failure-${hrid}`, { detail: error });
};

export const deleteRecordSuccessEvent = (hrid: string) => {
    return new CustomEvent(`delete-record-success-${hrid}`);
};

export const deleteRecordFailureEvent = (hrid: string, error: unknown) => {
    return new CustomEvent(`delete-record-failure-${hrid}`, { detail: error });
};

export const triggerWorkflowSuccessEvent = (hrid: string) => {
    return new CustomEvent(`trigger-workflow-success-${hrid}`);
};

export const triggerWorkflowFailureEvent = (hrid: string, error: unknown) => {
    return new CustomEvent(`trigger-workflow-failure-${hrid}`, { detail: error });
};

export const triggerWebhookSuccessEvent = (hrid: string, response: unknown) => {
    return new CustomEvent(`call-api-success-${hrid}`, { detail: { response } });
};

export const triggerWebhookFailureEvent = (hrid: string, error: unknown) => {
    return new CustomEvent(`call-api-failure-${hrid}`, { detail: error });
};

export const downloadFileActionSuccessEvent = (hrid: string, payload: { url?: string; filename?: string }) => {
    return new CustomEvent(`download-file-action-success-${hrid}`, { detail: payload });
};

export const downloadFileActionFailureEvent = (hrid: string, error: unknown) => {
    return new CustomEvent(`download-file-action-failure-${hrid}`, { detail: error });
};

export const exportAsActionSuccessEvent = (hrid: string, payload: Record<string, string>[]) => {
    return new CustomEvent(`export-as-action-success-${hrid}`, { detail: payload });
};

export const exportAsActionFailureEvent = (hrid: string, error: unknown) => {
    return new CustomEvent(`export-as-action-failure-${hrid}`, { detail: error });
};

export const chartLoadedEvent = (hrid: string, chart: unknown) => {
    return new CustomEvent(`chart-loaded-${hrid}`, { detail: chart });
};

export const userSignOutEvent = () => {
    return new CustomEvent("user-sign-out");
};

export const tabSelectedEvent = (hrid: string, tabHrid: string) => {
    return new CustomEvent(`tab-selected-${hrid}`, { detail: tabHrid });
};

export const recordModalOpenedEvent = (actionId: string) => {
    return new CustomEvent(`record-dialog-opened-${actionId}`, { detail: { actionId } });
};

export const openGlobalSearch = () => {
    return new CustomEvent("open-global-search");
};

export const openAgentDialog = () => {
    return new CustomEvent("open-agent-dialog");
};

export interface ModalCurrentRecordData {
    data: RecordItem;
    blockId: string | null;
}

export const modalCurrentRecordEvent = (detail: ModalCurrentRecordData | null) => {
    return new CustomEvent("modal-current-record", { detail });
};
